"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SyncAtbWeightRangesResponse = exports.AtbWeightRangesStatus = exports.AtbWeightRangeDeltaItem = exports.AtbWeightRangeRecord = exports.ATB_WEIGHT_RANGES_PAGE_MAX = exports.ATB_WEIGHT_RANGES_PAGE_DEFAULT = exports.ATB_WEIGHT_RANGES_ROUTE = exports.AtbWeightDatabaseView = exports.AtbWeightDatabaseState = exports.AtbWeightDatabaseTestResult = exports.WinScoErrorCode = exports.AtbWeightDatabaseTestInput = exports.AtbWeightDatabaseSettingsInput = exports.WinScoSchemaMapping = exports.WINSCO_IDENTIFIER_PATTERN = exports.ATB_WEIGHT_DATABASE_ONE_ONLY_MESSAGE = exports.ATB_WEIGHT_DATABASE_PASSWORD_MAX_LENGTH = exports.ATB_WEIGHT_DATABASE_DEFAULT_REFRESH_MINUTES = exports.ATB_WEIGHT_DATABASE_DEFAULT_PORT = exports.ATB_WEIGHT_DATABASE_DEFAULT_MODE = exports.AtbWeightDatabaseMode = void 0;
const zod_1 = require("zod");
const sync_js_1 = require("./sync.js");
/**
 * The ATB store's one security-weight database (ATBL-449): the lane's own
 * weights ("SQLite") or the legacy WinSCOServer database on Microsoft SQL,
 * read by Back Office and delivered to the lanes.
 *
 * The contract is written down in the terminals repository,
 * `reform-lite-terminals/docs/packages/atb-weight-database.md`; this file is
 * its wire half. When the two disagree, the document is corrected first.
 *
 * ## Exactly one database
 *
 * The choice is a single enum, so "both" cannot be expressed: not in the
 * store setting, not in a parameter import (the terminal parameter
 * `atb.weightDatabase` is machine-owned), not on the wire.
 *
 * ## The password
 *
 * Write-only. It appears in the `PUT` body ({@link AtbWeightDatabaseSettingsInput})
 * and in the test request, and in no response, feed or export: reads say only
 * {@link AtbWeightDatabaseView.passwordSet}. It never reaches a lane.
 */
exports.AtbWeightDatabaseMode = zod_1.z.enum(['sqlite', 'mssql']);
exports.ATB_WEIGHT_DATABASE_DEFAULT_MODE = 'sqlite';
exports.ATB_WEIGHT_DATABASE_DEFAULT_PORT = 1433;
exports.ATB_WEIGHT_DATABASE_DEFAULT_REFRESH_MINUTES = 5;
exports.ATB_WEIGHT_DATABASE_PASSWORD_MAX_LENGTH = 128;
/** The refusal of a mode that is not exactly one database. */
exports.ATB_WEIGHT_DATABASE_ONE_ONLY_MESSAGE = 'Only one weight database may be active';
/**
 * A SQL Server identifier the connector may put into its one statement:
 * letters, digits and underscores, not starting with a digit. Bracket-quoted
 * by the connector; never taken from row data.
 */
exports.WINSCO_IDENTIFIER_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,127}$/;
const Identifier = zod_1.z.string().regex(exports.WINSCO_IDENTIFIER_PATTERN, 'not a plain SQL identifier');
/** `table` or `schema.table`. */
const TableName = zod_1.z
    .string()
    .max(257)
    .refine((value) => value.split('.').length <= 2 &&
    value.split('.').every((part) => exports.WINSCO_IDENTIFIER_PATTERN.test(part)), 'not a plain table name (table or schema.table)');
/**
 * Where WinSCOServer keeps the weight ranges (contract §5). Not known yet:
 * ATB has sent connection strings only. Null on a store means "not
 * configured", and the connector then refuses to run rather than guess.
 */
exports.WinScoSchemaMapping = zod_1.z
    .object({
    table: TableName,
    columns: zod_1.z
        .object({
        itemCode: Identifier,
        minWeight: Identifier.optional(),
        maxWeight: Identifier.optional(),
        expectedWeight: Identifier.optional(),
        tolerance: Identifier.optional(),
        light: Identifier.optional(),
        bulky: Identifier.optional(),
        noCheck: Identifier.optional(),
        sourceLane: Identifier.optional(),
        changedAt: Identifier.optional(),
    })
        .strict()
        .refine((columns) => (columns.minWeight === undefined) === (columns.maxWeight === undefined), 'minWeight and maxWeight go together')
        .refine((columns) => columns.minWeight !== undefined || columns.expectedWeight !== undefined, 'a range (minWeight + maxWeight) or expectedWeight is required'),
    weightUnit: zod_1.z.enum(['g', 'kg', 'mg']),
    filter: zod_1.z
        .object({ column: Identifier, value: zod_1.z.string().min(1).max(128) })
        .strict()
        .optional(),
})
    .strict();
/** `PUT /weight/atb-database/stores/:locationId`. */
exports.AtbWeightDatabaseSettingsInput = zod_1.z
    .object({
    mode: exports.AtbWeightDatabaseMode,
    server: zod_1.z.string().trim().min(1).max(255).optional(),
    port: zod_1.z.number().int().min(1).max(65535).optional(),
    databaseName: zod_1.z.string().trim().min(1).max(128).optional(),
    login: zod_1.z.string().trim().min(1).max(128).optional(),
    /** Replace. Write-only: never returned. Absent keeps the stored one. */
    password: zod_1.z.string().min(1).max(exports.ATB_WEIGHT_DATABASE_PASSWORD_MAX_LENGTH).optional(),
    refreshIntervalMinutes: zod_1.z.number().int().min(1).max(1440).optional(),
    writeBack: zod_1.z.boolean().optional(),
    /** Null clears the mapping; absent keeps it. */
    schema: exports.WinScoSchemaMapping.nullable().optional(),
})
    .strict();
/** `POST …/test`: the settings to try, over the stored ones. Saves nothing. */
exports.AtbWeightDatabaseTestInput = zod_1.z
    .object({
    server: zod_1.z.string().trim().min(1).max(255).optional(),
    port: zod_1.z.number().int().min(1).max(65535).optional(),
    databaseName: zod_1.z.string().trim().min(1).max(128).optional(),
    login: zod_1.z.string().trim().min(1).max(128).optional(),
    password: zod_1.z.string().min(1).max(exports.ATB_WEIGHT_DATABASE_PASSWORD_MAX_LENGTH).optional(),
})
    .strict();
/** Fixed connection error codes; their text never echoes the driver. */
exports.WinScoErrorCode = zod_1.z.enum([
    'loginFailed',
    'unreachable',
    'timeout',
    'databaseNotFound',
    'tls',
    'failed',
    'passwordMissing',
    'schemaNotConfigured',
    'readFailed',
]);
exports.AtbWeightDatabaseTestResult = zod_1.z.union([
    zod_1.z.object({ ok: zod_1.z.literal(true) }),
    zod_1.z.object({ ok: zod_1.z.literal(false), code: exports.WinScoErrorCode, message: zod_1.z.string() }),
]);
exports.AtbWeightDatabaseState = zod_1.z.enum([
    'off',
    'neverRefreshed',
    'ok',
    'error',
    'schemaNotConfigured',
]);
/** What every read of a store setting returns. Never the password. */
exports.AtbWeightDatabaseView = zod_1.z.object({
    locationId: zod_1.z.string(),
    mode: exports.AtbWeightDatabaseMode,
    server: zod_1.z.string().nullable(),
    port: zod_1.z.number().int(),
    databaseName: zod_1.z.string().nullable(),
    login: zod_1.z.string().nullable(),
    passwordSet: zod_1.z.boolean(),
    refreshIntervalMinutes: zod_1.z.number().int(),
    writeBack: zod_1.z.boolean(),
    schema: exports.WinScoSchemaMapping.nullable(),
    status: zod_1.z.object({
        state: exports.AtbWeightDatabaseState,
        lastTestOkAt: zod_1.z.string().datetime().nullable(),
        lastAttemptAt: zod_1.z.string().datetime().nullable(),
        lastRefreshOkAt: zod_1.z.string().datetime().nullable(),
        lastErrorCode: exports.WinScoErrorCode.nullable(),
        lastError: zod_1.z.string().nullable(),
        recordCount: zod_1.z.number().int().nonnegative(),
    }),
    updatedAt: zod_1.z.string().datetime().nullable(),
});
// ---------- Sync: sync.atb-weight-ranges ----------
/** `GET /sync/atb-weight-ranges`. */
exports.ATB_WEIGHT_RANGES_ROUTE = 'atb-weight-ranges';
exports.ATB_WEIGHT_RANGES_PAGE_DEFAULT = 1000;
exports.ATB_WEIGHT_RANGES_PAGE_MAX = 5000;
const Mg = zod_1.z.number().int().nonnegative();
/** One item's range as WinSCOServer holds it, in integer milligrams. */
exports.AtbWeightRangeRecord = zod_1.z.object({
    itemCode: zod_1.z.string().min(1).max(64),
    minMg: Mg.nullable(),
    maxMg: Mg.nullable(),
    expectedMg: Mg.nullable(),
    toleranceMg: Mg.nullable(),
    light: zod_1.z.boolean(),
    bulky: zod_1.z.boolean(),
    noCheck: zod_1.z.boolean(),
    sourceLane: zod_1.z.string().max(128).nullable(),
    sourceChangedAt: zod_1.z.string().datetime().nullable(),
});
/** `id` is the item code. */
exports.AtbWeightRangeDeltaItem = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({ kind: zod_1.z.literal('create'), id: zod_1.z.string(), payload: exports.AtbWeightRangeRecord }),
    zod_1.z.object({ kind: zod_1.z.literal('update'), id: zod_1.z.string(), payload: exports.AtbWeightRangeRecord }),
    zod_1.z.object({ kind: zod_1.z.literal('delete'), id: zod_1.z.string() }),
]);
/** What a lane is told about its store's weight database. No error text, no credential. */
exports.AtbWeightRangesStatus = zod_1.z.object({
    mode: exports.AtbWeightDatabaseMode,
    databaseName: zod_1.z.string().nullable(),
    writeBack: zod_1.z.boolean(),
    state: exports.AtbWeightDatabaseState,
    lastRefreshOkAt: zod_1.z.string().datetime().nullable(),
    lastAttemptAt: zod_1.z.string().datetime().nullable(),
    recordCount: zod_1.z.number().int().nonnegative(),
});
exports.SyncAtbWeightRangesResponse = zod_1.z.object({
    /** The calling terminal's store, never a WinSCOServer id. */
    locationId: zod_1.z.string().min(1).nullable(),
    status: exports.AtbWeightRangesStatus,
    items: zod_1.z.array(exports.AtbWeightRangeDeltaItem),
    /** `<revision>:<id>`; opaque to the lane. */
    cursor: sync_js_1.SyncCursor,
    hasMore: zod_1.z.boolean(),
    snapshotAt: zod_1.z.string().datetime(),
});
//# sourceMappingURL=atb-weight-database.js.map